import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import ReactSwitch from 'react-switch';
import {
  deleteAddress,
  getContactAddress,
  saveAddress,
} from '../../../services/address/addressService';
import APIError from '../../../services/APIError';
import CustomerAddress from '../../../models/CustomerAddress';
import styles from './AddressInformation.module.css';
import Input from '../../common/Input/Input';
import Customer from '../../../models/Customer';
import { isAllFieldsEmpty, isValidAddress } from '../../../utils/validation';
import commonStyles from '../common.module.css';
import Error from '../../common/Error/Error';
import warningLogo from '../../../assets/images/warning.png';
import { getTimeout } from '../../../utils/util';
import successLogo from '../../../assets/images/success.png';
import CountrySelect from '../../common/CountrySelect/CountrySelect';
import { getCountryByValue } from '../../common/CountrySelect/countries';


type AddressInformationProps = {
  customer: Customer
}

const initAddress: CustomerAddress = {
  addressType: '',
  streetHouse: '',
  country: '',
  city: '',
  postalCode: '',
};

function AddressInformation(props: AddressInformationProps) {
  const { t } = useTranslation();
  const [contactAddress, setContactAddress] = useState<CustomerAddress>();
  const [residentialAddress, setResidentialAddress] = useState<CustomerAddress>();
  const [isEdit, setIsEdit] = useState(false);
  const [newResidentialAddress, setNewResidentialAddress] = useState<CustomerAddress>(initAddress);
  const [newContactAddress, setNewContactAddress] = useState<CustomerAddress>(initAddress);
  const [isSameAddress, setIsSameAddress] = useState(false);
  const [contactAddressError, setContactAddressError] = useState(false);
  const [residentialAddressError, setResidentialAddressError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsSuccess(false);
    }, getTimeout());
    return () => {
      clearTimeout(timer);
    };
  }, [isSuccess]);

  const getContact = async () => {
    try {
      const contact = await getContactAddress({
        addressType: 'CONTACT',
        customerId: props.customer.id!,
      });
      setContactAddress(contact);
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const getResidential = async () => {
    try {
      const legalAddress = await getContactAddress({
        addressType: 'RESIDENTIAL',
        customerId: props.customer.id!,
      });
      setResidentialAddress(legalAddress);
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const save = async (address: CustomerAddress) => {
    try {
      await saveAddress({ ...address, person: props.customer });
    } catch (err) {
      if (err instanceof APIError) {
        console.log(err.errorBody);
      } else {
        console.log(err);
      }
    }
  };

  const remove = async (address: CustomerAddress) => {
    try {
      await deleteAddress({ addressId: address.id! });
    } catch (err) {
      if (err instanceof APIError) {
        console.log(`Failed to delete representative: ${err.errorBody}`);
      } else {
        console.log(err);
      }
    }
  };

  const saveOrRemove = (address: CustomerAddress) => {
    if (isAllFieldsEmpty(address)) {
      if (address.id) {
        remove(address);
      }
    } else {
      save(address);
    }
  };

  const updateResidentialAddress = (address: CustomerAddress) => {
    setNewResidentialAddress(address);
  };

  const updateContactAddress = (address: CustomerAddress) => {
    setNewContactAddress(address);
  };

  useEffect(() => {
    getContact();
    getResidential();
  }, [props.customer.id]);

  useEffect(() => {
    if (residentialAddress) {
      setNewResidentialAddress(residentialAddress);
    }
  }, [residentialAddress]);

  useEffect(() => {
    if (contactAddress) {
      setNewContactAddress(contactAddress);
    }
  }, [contactAddress]);

  const handleCancelButton = () => {
    setIsSameAddress(false);
    setIsEdit(false);
    setContactAddressError(false);
    setResidentialAddressError(false);
  };

  const saveResidentialAddress = () => {
    if (isValidAddress(newResidentialAddress)) {
      saveOrRemove({ ...newResidentialAddress, addressType: 'RESIDENTIAL' });
      setNewResidentialAddress({ ...initAddress, addressType: 'RESIDENTIAL' });
      setIsEdit(false);
      setIsSuccess(true);
      setTimeout(() => {
        getResidential();
      }, 500);
      setResidentialAddressError(false);
    } else {
      setResidentialAddressError(true);
    }
  };

  const saveContactAddress = () => {
    if (isValidAddress(newContactAddress)) {
      saveOrRemove({ ...newContactAddress, addressType: 'CONTACT' });
      setNewContactAddress({ ...initAddress, addressType: 'CONTACT' });
      setTimeout(() => {
        getContact();
      }, 500);
      setContactAddressError(false);
    } else {
      setContactAddressError(true);
    }
  };

  const saveSameAddress = () => {
    if (isValidAddress(newResidentialAddress)) {
      saveOrRemove({
        id: newContactAddress ? newContactAddress.id : undefined,
        addressType: 'CONTACT',
        streetHouse: newResidentialAddress.streetHouse,
        country: newResidentialAddress.country,
        city: newResidentialAddress.city,
        postalCode: newResidentialAddress.postalCode,
      });
      setNewContactAddress({ ...initAddress, addressType: 'CONTACT' });
      setTimeout(() => {
        getContact();
      }, 500);
    }
  };

  const handleSaveButton = () => {
    saveResidentialAddress();
    if (isSameAddress || isAllFieldsEmpty(newContactAddress)) {
      saveSameAddress();
    } else {
      saveContactAddress();
    }
    setIsSameAddress(false);
  };

  return (
    <div style={{ position: 'relative' }}>
      <div className={styles.buttonContainer}>
        {!isEdit && (
          <button
            type='button'
            className={styles.button}
            onClick={() => setIsEdit(true)}
          >
            {t('common.edit')}
          </button>
        )}
        {isEdit && (
          <div>
            <button
              type='button'
              className={styles.button}
              onClick={handleSaveButton}
            >
              {t('common.save')}
            </button>
            <button
              type='button'
              className={styles.button}
              onClick={handleCancelButton}
            >
              {t('common.cancel')}
            </button>
          </div>
        )}
      </div>
      <div className={commonStyles.header}>
        {(contactAddressError || residentialAddressError) && (
          <div className={styles.errorMessageContainer}>
            <Error
              messageText={t(`customer.validation.addressEditFailed`)}
              logoSrc={warningLogo}
              logoAltText={t('customer.validation.warningLogo')}
            />
          </div>
        )}
        {isSuccess &&
          <div className={styles.successMessage}>
            <img className={styles.logo} src={successLogo}
                 alt={t('customer.dashboard.successLogo')} />
            <p>{t('customer.dashboard.addressSuccessMessage')}</p>
          </div>
        }
      </div>
      <div className={classNames(styles.container, isEdit && styles.editContainer)}>
        <div className={styles.child}>
          <div className={classNames(styles.label, styles.longLabel)}>
            {t('customer.dashboard.addressInformation.label.legalAddress')}
          </div>
          {!isEdit && residentialAddress && (
            <div>
              {residentialAddress.streetHouse}
              {' '}
              {residentialAddress.postalCode}
              {' '}
              {residentialAddress.city}
              {' '}
              {getCountryByValue(residentialAddress.country)}
            </div>
          )}
          {isEdit && (
            <>
              <div className={styles.inputFields}>
                <div>
                  <Input
                    id='legalStreet'
                    label={t('customer.dashboard.addressInformation.label.street')}
                    isLongLabel
                    placeholder={t('customer.dashboard.addressInformation.placeholder.street')}
                    type='text'
                    maxLength={70}
                    required
                    value={newResidentialAddress.streetHouse}
                    onUpdate={(value) => updateResidentialAddress({
                      ...newResidentialAddress,
                      streetHouse: value,
                    })}
                    error={residentialAddressError && newResidentialAddress.streetHouse === ''}
                  />
                </div>
                <div>
                  <Input
                    id='legalCity'
                    label={t('customer.dashboard.addressInformation.label.city')}
                    placeholder={t('customer.dashboard.addressInformation.placeholder.city')}
                    type='text'
                    maxLength={35}
                    value={newResidentialAddress.city}
                    required
                    onUpdate={(value) => updateResidentialAddress({
                      ...newResidentialAddress,
                      city: value,
                    })}
                    error={residentialAddressError && newResidentialAddress.city === ''}
                  />
                </div>
                <div>
                  <CountrySelect
                    required
                    label={t('customer.dashboard.addressInformation.label.country')}
                    value={newResidentialAddress.country}
                    onUpdate={(value) => updateResidentialAddress({
                      ...newResidentialAddress,
                      country: value,
                    })}
                    error={residentialAddressError && newResidentialAddress.country === ''}
                  />
                </div>
                <div>
                  <Input
                    id='legalPostalCode'
                    label={t('customer.dashboard.addressInformation.label.postalCode')}
                    placeholder={t('customer.dashboard.addressInformation.placeholder.postalCode')}
                    type='text'
                    maxLength={16}
                    value={newResidentialAddress.postalCode}
                    width={110}
                    required
                    onUpdate={(value) => updateResidentialAddress({
                      ...newResidentialAddress,
                      postalCode: value,
                    })}
                    error={residentialAddressError && newResidentialAddress.postalCode === ''}
                  />
                </div>
              </div>
              <div className={styles.switch}>
                <ReactSwitch
                  checked={isSameAddress}
                  onChange={() => setIsSameAddress(prevState => !prevState)}
                  onColor='#86d3ff'
                  onHandleColor='#2693e6'
                  handleDiameter={30}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  boxShadow='0px 1px 5px rgba(0, 0, 0, 0.6)'
                  activeBoxShadow='0px 0px 1px 10px rgba(0, 0, 0, 0.2)'
                  height={20}
                  width={48}
                  className={styles.switcher}
                />
                <p>{t('customer.dashboard.addressInformation.sameAddress')}</p>
              </div>
            </>
          )}
        </div>
        {!isSameAddress && (
          <div className={styles.child}>
            <div className={classNames(styles.label, styles.longLabel)}>
              {t('customer.dashboard.addressInformation.label.contactAddress')}
            </div>
            {!isEdit && contactAddress && (
              <div>
                {contactAddress.streetHouse}
                {' '}
                {contactAddress.postalCode}
                {' '}
                {contactAddress.city}
                {' '}
                {getCountryByValue(contactAddress.country)}
              </div>
            )}
            {isEdit && (
              <div className={styles.inputFields}>
                <div>
                  <Input
                    id='contactStreet'
                    label={t('customer.dashboard.addressInformation.label.street')}
                    isLongLabel
                    placeholder={t('customer.dashboard.addressInformation.placeholder.street')}
                    type='text'
                    value={newContactAddress.streetHouse}
                    maxLength={70}
                    required
                    onUpdate={(value) => updateContactAddress({
                      ...newContactAddress,
                      streetHouse: value,
                    })}
                    error={contactAddressError && newContactAddress.streetHouse === ''}
                  />
                </div>
                <div>
                  <Input
                    id='contactCity'
                    label={t('customer.dashboard.addressInformation.label.city')}
                    placeholder={t('customer.dashboard.addressInformation.placeholder.city')}
                    type='text'
                    maxLength={35}
                    value={newContactAddress.city}
                    required
                    onUpdate={(value) => updateContactAddress({
                      ...newContactAddress,
                      city: value,
                    })}
                    error={contactAddressError && newContactAddress.city === ''}
                  />
                </div>
                <div>
                  <CountrySelect
                    required
                    label={t('customer.dashboard.addressInformation.label.country')}
                    value={newContactAddress.country}
                    onUpdate={(value) => updateContactAddress({
                      ...newContactAddress,
                      country: value,
                    })}
                    error={contactAddressError && newContactAddress.country === ''}
                  />
                </div>
                <div>
                  <Input
                    id='contactPostalCode'
                    label={t('customer.dashboard.addressInformation.label.postalCode')}
                    placeholder={t('customer.dashboard.addressInformation.placeholder.postalCode')}
                    type='text'
                    maxLength={16}
                    value={newContactAddress.postalCode}
                    width={110}
                    required
                    onUpdate={(value) => updateContactAddress({
                      ...newContactAddress,
                      postalCode: value,
                    })}
                    error={contactAddressError && newContactAddress.postalCode === ''}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

  );
}

export default AddressInformation;
